import { memo, type ReactNode } from 'react';

import { TableCell } from '~/components/TableView/TableCell';
import { useTableView } from '~/components/TableView/TableViewContext';
import { makeActiveTableColumnFromOption } from '~/components/TableView/tableColumnWidthsUtils';
import { rightAlignedTableCells } from '~/components/TableView/types';
import { ConfigVisibilityToggleType, getVisibilityToggleTypeFromString } from '~/constants/configViews';
import { configViewOptionsSelector } from '~/store/configViews/selectors';
import { type ConfigViewOption } from '~/store/configViews/types';
import { useAirSelector } from '~/utils/ReduxUtils';

const TABLE_ROW_CELL = 'TABLE_ROW_CELL';

export type ConfigurableTableRowsProps = {
  /** When set, render these columns instead of the shared config view. */
  options?: ConfigViewOption[];
  rowInfo: Record<ConfigVisibilityToggleType, string>;
  renderCustomFieldTableCellComponent: (option: ConfigViewOption) => ReactNode;
  actorAvatar?: ReactNode;
};

export const ConfigurableTableCells = memo(
  ({ options, rowInfo, renderCustomFieldTableCellComponent, actorAvatar }: ConfigurableTableRowsProps) => {
    const storeOptions = useAirSelector(configViewOptionsSelector);
    const configViewOptions = options ?? storeOptions;
    const { getColumnWidthStyle } = useTableView();

    return (
      <>
        {configViewOptions.map((option) => {
          if (option.isCustomField && option.customFieldId && option.isVisible) {
            return renderCustomFieldTableCellComponent(option);
          }

          const toggleType = getVisibilityToggleTypeFromString(option.fieldName);

          if (!toggleType || !option.isVisible) {
            return null;
          }

          const tableCellProps = {
            'data-testid': `${TABLE_ROW_CELL}-${option.fieldName}`,
            style: getColumnWidthStyle(
              makeActiveTableColumnFromOption(option.fieldName, option.isCustomField, option.customFieldId),
            ),
            // !important added here to override padding set in `DesktopTableRowContainer`
            className: rightAlignedTableCells.has(toggleType) ? 'text-right !pr-4' : 'text-left',
          };
          const cellValue = rowInfo[toggleType];

          if (toggleType === ConfigVisibilityToggleType.uploadedBy) {
            return (
              <TableCell key={option.fieldName} {...tableCellProps}>
                <div className="flex w-full items-center gap-2">
                  {actorAvatar}
                  <span className="truncate">{cellValue}</span>
                </div>
              </TableCell>
            );
          }

          return (
            <TableCell key={option.fieldName} {...tableCellProps}>
              {cellValue}
            </TableCell>
          );
        })}
      </>
    );
  },
);

ConfigurableTableCells.displayName = 'ConfigurableTableRows';
